import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { ErrorRecovery } from '@/components/ErrorRecovery';
import { LoadingOverlay } from '@/components/LoadingSkeletons';
import { type AppError } from '@/lib/errors';

interface ErrorContextType {
  showError: (error: AppError | Error | string, options?: ErrorDisplayOptions) => void;
  hideError: () => void;
  showLoading: (message?: string, variant?: 'default' | 'minimal' | 'fullscreen') => void;
  hideLoading: () => void;
  isLoading: boolean;
  currentError: AppError | Error | string | null;
}

interface ErrorDisplayOptions {
  variant?: 'inline' | 'card' | 'page';
  showDetails?: boolean;
  autoRetry?: boolean;
  maxAutoRetries?: number;
  onRetry?: () => Promise<void> | void;
  onDismiss?: () => void;
}

interface ErrorState {
  error: AppError | Error | string | null;
  options: ErrorDisplayOptions;
}

interface LoadingState {
  isLoading: boolean;
  message: string;
  variant: 'default' | 'minimal' | 'fullscreen';
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}

interface ErrorProviderProps {
  children: ReactNode;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    options: {},
  });
  
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false,
    message: 'Loading...',
    variant: 'default',
  });

  const showError = useCallback((
    error: AppError | Error | string, 
    options: ErrorDisplayOptions = {}
  ) => {
    setErrorState({
      error,
      options: {
        variant: 'card',
        showDetails: false,
        autoRetry: false,
        maxAutoRetries: 3,
        ...options,
      },
    });
  }, []);

  const hideError = useCallback(() => {
    setErrorState({
      error: null,
      options: {},
    });
  }, []);

  const showLoading = useCallback((
    message = 'Loading...', 
    variant: 'default' | 'minimal' | 'fullscreen' = 'default'
  ) => {
    setLoadingState({
      isLoading: true,
      message,
      variant,
    });
  }, []);

  const hideLoading = useCallback(() => {
    setLoadingState(prev => ({
      ...prev,
      isLoading: false,
    }));
  }, []);

  const contextValue: ErrorContextType = {
    showError,
    hideError,
    showLoading,
    hideLoading,
    isLoading: loadingState.isLoading,
    currentError: errorState.error,
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
      
      {/* Global error display */}
      {errorState.error && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="max-w-lg w-full">
            <ErrorRecovery
              error={errorState.error}
              variant={errorState.options.variant}
              showDetails={errorState.options.showDetails}
              autoRetry={errorState.options.autoRetry}
              maxAutoRetries={errorState.options.maxAutoRetries}
              onRetry={errorState.options.onRetry}
              onDismiss={errorState.options.onDismiss || hideError}
            />
          </div>
        </div>
      )}
      
      {/* Global loading overlay */}
      {loadingState.isLoading && (
        <LoadingOverlay
          message={loadingState.message}
          variant={loadingState.variant}
        />
      )}
    </ErrorContext.Provider>
  );
}

// Convenience hooks for common error scenarios
export function useApiError() {
  const { showError } = useError();
  
  return useCallback((error: unknown) => {
    showError(error as AppError | Error | string, {
      variant: 'card',
      autoRetry: true,
      maxAutoRetries: 3,
    });
  }, [showError]);
}

export function usePageError() {
  const { showError } = useError();
  
  return useCallback((error: unknown) => {
    showError(error as AppError | Error | string, {
      variant: 'page',
      showDetails: process.env.NODE_ENV === 'development',
    });
  }, [showError]);
}

export function useLoadingOverlay() {
  const { showLoading, hideLoading, isLoading } = useError();
  
  const withLoading = useCallback(
    async (
      asyncOperation: () => Promise<any>,
      message?: string,
      variant?: 'default' | 'minimal' | 'fullscreen'
    ): Promise<any> => {
      showLoading(message, variant);
      try {
        const result = await asyncOperation();
        return result;
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading]
  );

  return {
    showLoading,
    hideLoading,
    isLoading,
    withLoading,
  };
}